import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { MdPeople } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioCliente } from "../../sync/repositories/financeiro";

const AJUDA = {
  titulo: "Cadastro de Clientes",
  descricao:
    "Cadastre aqui as pessoas ou empresas que compram produtos ou serviços da fazenda. Esse cadastro é usado depois nas Contas a Receber e pode ser vinculado como cedente nas Contas Bancárias — quanto mais completo, mais fácil localizar o cliente e conferir o histórico de vendas.",
  secoes: [
    {
      titulo: "Tipo de Pessoa",
      campos: [
        {
          nome: "Pessoa Jurídica (CNPJ) / Pessoa Física (CPF)",
          obrigatorio: true,
          explicacao:
            "Escolha primeiro se o cliente é uma empresa (Jurídica) ou uma pessoa (Física) — isso muda quais campos aparecem em 'Dados Principais'.",
        },
      ],
    },
    {
      titulo: "Dados Principais",
      campos: [
        {
          nome: "Razão Social",
          obrigatorio: true,
          explicacao: "Nome oficial da empresa, exatamente como consta no CNPJ. Só aparece para Pessoa Jurídica.",
        },
        {
          nome: "Nome Fantasia",
          obrigatorio: false,
          explicacao: "Nome popular/comercial da empresa, se for diferente da razão social.",
        },
        {
          nome: "CNPJ",
          obrigatorio: true,
          explicacao: "Documento da empresa. Só aparece para Pessoa Jurídica.",
        },
        {
          nome: "Nome Completo",
          obrigatorio: true,
          explicacao: "Nome completo da pessoa. Só aparece para Pessoa Física.",
        },
        {
          nome: "CPF",
          obrigatorio: true,
          explicacao: "Documento da pessoa. Só aparece para Pessoa Física.",
        },
        {
          nome: "Status",
          obrigatorio: false,
          explicacao:
            "Ativo (compra normalmente), Inativo ou Bloqueado. Clientes inativos continuam no histórico, mas somem das listas de seleção em novos lançamentos.",
        },
      ],
    },
    {
      titulo: "Endereço",
      campos: [
        {
          nome: "CEP",
          obrigatorio: false,
          explicacao: "Digite o CEP e clique em 'Buscar' (ou saia do campo) para preencher automaticamente logradouro, bairro, cidade e estado.",
        },
      ],
    },
    {
      titulo: "Contato",
      campos: [
        {
          nome: "Telefone / Celular / E-mail",
          obrigatorio: false,
          explicacao: "Formas de contato direto com o cliente.",
        },
      ],
    },
    {
      titulo: "Comercial",
      campos: [
        {
          nome: "Limite de Crédito / Condição de Pagamento",
          obrigatorio: false,
          explicacao:
            "Informações de referência sobre o crédito e prazo negociado com esse cliente — servem de consulta, mas não são copiadas automaticamente para as Contas a Receber.",
        },
      ],
    },
    {
      titulo: "Outras Informações",
      campos: [
        {
          nome: "Observações",
          obrigatorio: false,
          explicacao: "Anotações gerais sobre o cliente.",
        },
      ],
    },
  ],
  dicas: [
    "Excluir um cliente com contas a receber ou movimentações vinculadas pode não ser permitido pelo sistema — nesse caso, prefira apenas mudar o status para 'Inativo' em vez de excluir.",
    "O cliente cadastrado aqui também aparece como opção de 'Cedente' no cadastro de Contas Bancárias.",
  ],
};

export default function GestaoClientes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState("dados");
  const [mensagem, setMensagem] = useState("");
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);
  
  const [filtros, setFiltros] = useState({
    busca: "",
    status: ""
  });
  
  const [cliente, setCliente] = useState({
    tipo_pessoa: "J",
    razao_social: "",
    nome_fantasia: "",
    cnpj: "",
    insc_est: "",
    insc_municipal: "",
    nome_completo: "",
    cpf: "",
    rg: "",
    orgao_expeditor: "",
    data_nascimento: "",
    sexo: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    pais: "Brasil",
    telefone: "",
    celular: "",
    email: "",
    limite_credito: "0",
    condicao_pagamento: "",
    status: "A",
    observacoes: "",
  });

  // Estados brasileiros
  const estados = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
    "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
    "SP", "SE", "TO"
  ];

  const sexoOptions = [
    { value: "M", label: "Masculino" },
    { value: "F", label: "Feminino" },
    { value: "O", label: "Outro" },
  ];

  const statusList = [
    { value: "A", label: "Ativo", cor: "bg-green-100 text-green-800" },
    { value: "I", label: "Inativo", cor: "bg-red-100 text-red-800" },
    { value: "B", label: "Bloqueado", cor: "bg-gray-100 text-gray-800" },
  ];

  // Carregar clientes
  const carregarClientes = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioCliente.listar();
      const dados = response.data.results || response.data;
      setClientes(Array.isArray(dados) ? dados : []);
      setClientesFiltrados(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      alert("Erro ao carregar lista de clientes");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarClientes();
  }, [carregarClientes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCliente(prev => ({ ...prev, [name]: value }));
  };

  const handleTipoPessoaChange = (tipo) => {
    setCliente(prev => ({ ...prev, tipo_pessoa: tipo }));
  };

  const buscarCep = async () => {
    if (!cliente.cep || cliente.cep.length < 8) return;

    try {
      const cepLimpo = cliente.cep.replace(/\D/g, '');
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      
      if (!response.data.erro) {
        setCliente(prev => ({
          ...prev,
          logradouro: response.data.logradouro || "",
          bairro: response.data.bairro || "",
          cidade: response.data.localidade || "",
          estado: response.data.uf || "",
        }));
        setMensagem("");
      } else {
        setMensagem("CEP não encontrado");
        setTimeout(() => setMensagem(""), 3000);
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      setMensagem("Erro ao buscar CEP");
      setTimeout(() => setMensagem(""), 3000);
    }
  };

  const validarFormulario = () => {
    if (cliente.tipo_pessoa === "J") {
      if (!cliente.razao_social.trim()) {
        alert("Preencha a razão social.");
        return false;
      }
      if (!cliente.cnpj) {
        alert("Preencha o CNPJ.");
        return false;
      }
    } else {
      if (!cliente.nome_completo.trim()) {
        alert("Preencha o nome completo.");
        return false;
      }
      if (!cliente.cpf) {
        alert("Preencha o CPF.");
        return false;
      }
    }
    return true;
  };

  const formatarDataParaEnvio = (data) => {
    if (!data) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(data)) return data;
    try {
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(data)) {
        const [d, m, a] = data.split('/');
        return `${a}-${m}-${d}`;
      }
      const obj = new Date(data);
      if (!isNaN(obj.getTime())) {
        return `${obj.getFullYear()}-${String(obj.getMonth()+1).padStart(2,'0')}-${String(obj.getDate()).padStart(2,'0')}`;
      }
    } catch { /* ignore */ }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) return;

    setLoading(true);

    const payload = {
      ...cliente,
      data_nascimento: formatarDataParaEnvio(cliente.data_nascimento),
    };

    try {
      if (editando) {
        await repositorioCliente.atualizar(editandoId, payload);
        setMensagem("Cliente atualizado com sucesso!");
        handleCancel();
        carregarClientes();
      } else {
        await repositorioCliente.criar(payload);
        setMensagem("Cliente cadastrado com sucesso!");
        handleCancel();
        carregarClientes();
      }
      setTimeout(() => setMensagem(""), 3000);
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (clienteItem) => {
    setCliente({
      tipo_pessoa: clienteItem.tipo_pessoa || "J",
      razao_social: clienteItem.razao_social || "",
      nome_fantasia: clienteItem.nome_fantasia || "",
      cnpj: clienteItem.cnpj || "",
      insc_est: clienteItem.insc_est || "",
      insc_municipal: clienteItem.insc_municipal || "",
      nome_completo: clienteItem.nome_completo || "",
      cpf: clienteItem.cpf || "",
      rg: clienteItem.rg || "",
      orgao_expeditor: clienteItem.orgao_expeditor || "",
      data_nascimento: clienteItem.data_nascimento || "",
      sexo: clienteItem.sexo || "",
      cep: clienteItem.cep || "",
      logradouro: clienteItem.logradouro || "",
      numero: clienteItem.numero || "",
      complemento: clienteItem.complemento || "",
      bairro: clienteItem.bairro || "",
      cidade: clienteItem.cidade || "",
      estado: clienteItem.estado || "",
      pais: clienteItem.pais || "Brasil",
      telefone: clienteItem.telefone || "",
      celular: clienteItem.celular || "",
      email: clienteItem.email || "",
      limite_credito: clienteItem.limite_credito || "0",
      condicao_pagamento: clienteItem.condicao_pagamento || "",
      status: clienteItem.status || "A",
      observacoes: clienteItem.observacoes || "",
    });
    setEditando(true);
    setEditandoId(clienteItem.id);
    setMostrarFormulario(true);
    setAbaAtiva("dados");
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoCliente = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setCliente({
      tipo_pessoa: "J",
      razao_social: "",
      nome_fantasia: "",
      cnpj: "",
      insc_est: "",
      insc_municipal: "",
      nome_completo: "",
      cpf: "",
      rg: "",
      orgao_expeditor: "",
      data_nascimento: "",
      sexo: "",
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      pais: "Brasil",
      telefone: "",
      celular: "",
      email: "",
      limite_credito: "0",
      condicao_pagamento: "",
      status: "A",
      observacoes: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
    setAbaAtiva("dados");
    setMensagem("");
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioCliente.excluir(id);
      alert("Cliente excluído com sucesso!");
      carregarClientes();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir cliente. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(clientes) ? [...clientes] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c => 
        c.razao_social?.toLowerCase().includes(buscaLower) ||
        c.nome_fantasia?.toLowerCase().includes(buscaLower) ||
        c.nome_completo?.toLowerCase().includes(buscaLower) ||
        c.cnpj?.includes(buscaLower) ||
        c.cpf?.includes(buscaLower) ||
        c.email?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.status) {
      filtrados = filtrados.filter(c => c.status === filtros.status);
    }
    
    setClientesFiltrados(filtrados);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", status: "" });
    setClientesFiltrados(Array.isArray(clientes) ? clientes : []);
    setPaginaAtual(1);
  };

  // Paginação
  const indiceUltimoItem = paginaAtual * itensPorPagina;
  const indicePrimeiroItem = indiceUltimoItem - itensPorPagina;
  const clientesPaginados = clientesFiltrados.slice(indicePrimeiroItem, indiceUltimoItem);
  const totalPaginas = Math.ceil(clientesFiltrados.length / itensPorPagina);

  const formatarDocumento = (clienteItem) => {
    if (clienteItem.tipo_pessoa === "J") {
      return clienteItem.cnpj || "—";
    }
    return clienteItem.cpf || "—";
  };

  const getNomeCliente = (clienteItem) => {
    if (clienteItem.tipo_pessoa === "J") {
      return clienteItem.razao_social || clienteItem.nome_fantasia || "—";
    }
    return clienteItem.nome_completo || "—";
  };

  const getStatusInfo = (status) => {
    const statusInfo = statusList.find(s => s.value === status);
    return statusInfo || { label: status, cor: "bg-gray-100 text-gray-800" };
  };

  const formatarTelefone = (value) => {
    if (!value) return "";
    const telefone = value.replace(/\D/g, '');
    if (telefone.length === 11) {
      return telefone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    if (telefone.length === 10) {
      return telefone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const formatarCnpj = (value) => {
    if (!value) return "";
    const cnpj = value.replace(/\D/g, '');
    if (cnpj.length <= 14) {
      return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  };

  const formatarCpf = (value) => {
    if (!value) return "";
    const cpf = value.replace(/\D/g, '');
    if (cpf.length <= 11) {
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Clientes <MdPeople size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoCliente}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Cliente
            </button>
          )}
        </div>

        {/* Mensagem de feedback */}
        {mensagem && (
          <div className={`mb-4 p-3 rounded ${mensagem.includes("sucesso") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
            {mensagem}
          </div>
        )}
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Cliente" : "Cadastrar Novo Cliente"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              {/* Tipo de Pessoa */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="J"
                      checked={cliente.tipo_pessoa === "J"}
                      onChange={() => handleTipoPessoaChange("J")}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="font-medium">Pessoa Jurídica (CNPJ)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="F"
                      checked={cliente.tipo_pessoa === "F"}
                      onChange={() => handleTipoPessoaChange("F")}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="font-medium">Pessoa Física (CPF)</span>
                  </label>
                </div>
              </div>

              {/* Abas */}
              <div className="border-b mb-6">
                <nav className="flex gap-2 flex-wrap">
                  {[
                    { id: "dados", label: "Dados Principais" },
                    { id: "endereco", label: "Endereço" },
                    { id: "contato", label: "Contato" },
                    { id: "comercial", label: "Comercial" },
                    { id: "outros", label: "Outras Informações" }
                  ].map(aba => (
                    <button
                      key={aba.id}
                      type="button"
                      onClick={() => setAbaAtiva(aba.id)}
                      className={`px-4 py-2 rounded-t-lg transition-colors ${
                        abaAtiva === aba.id
                          ? "bg-green-600 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {aba.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Conteúdo das Abas */}
              <div className="space-y-4">
                {/* Aba Dados Principais */}
                {abaAtiva === "dados" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {cliente.tipo_pessoa === "J" ? (
                      <>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">Razão Social *</label>
                          <input
                            type="text"
                            name="razao_social"
                            value={cliente.razao_social}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            required
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">Nome Fantasia</label>
                          <input
                            type="text"
                            name="nome_fantasia"
                            value={cliente.nome_fantasia}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">CNPJ *</label>
                          <input
                            type="text"
                            name="cnpj"
                            value={formatarCnpj(cliente.cnpj)}
                            onChange={(e) => handleChange({ target: { name: "cnpj", value: e.target.value.replace(/\D/g, '') } })}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            maxLength="18"
                            required
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">Inscrição Estadual</label>
                          <input
                            type="text"
                            name="insc_est"
                            value={cliente.insc_est}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">Inscrição Municipal</label>
                          <input
                            type="text"
                            name="insc_municipal"
                            value={cliente.insc_municipal}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-col md:col-span-2">
                          <label className="text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                          <input
                            type="text"
                            name="nome_completo"
                            value={cliente.nome_completo}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            required
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">CPF *</label>
                          <input
                            type="text"
                            name="cpf"
                            value={formatarCpf(cliente.cpf)}
                            onChange={(e) => handleChange({ target: { name: "cpf", value: e.target.value.replace(/\D/g, '') } })}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            maxLength="14"
                            required
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">RG</label>
                          <input
                            type="text"
                            name="rg"
                            value={cliente.rg}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">Órgão Expeditor</label>
                          <input
                            type="text"
                            name="orgao_expeditor"
                            value={cliente.orgao_expeditor}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            placeholder="Ex: SSP/SP"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">Data de Nascimento</label>
                          <input
                            type="date"
                            name="data_nascimento"
                            value={cliente.data_nascimento}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">Sexo</label>
                          <select
                            name="sexo"
                            value={cliente.sexo}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          >
                            <option value="">Selecione</option>
                            {sexoOptions.map(sex => (
                              <option key={sex.value} value={sex.value}>{sex.label}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        name="status"
                        value={cliente.status}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      >
                        {statusList.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Aba Endereço */}
                {abaAtiva === "endereco" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">CEP</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="cep"
                          value={cliente.cep}
                          onChange={handleChange}
                          onBlur={buscarCep}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          placeholder="00000-000"
                        />
                        <button
                          type="button"
                          onClick={buscarCep}
                          className="bg-blue-600 text-white px-3 rounded hover:bg-blue-700 transition-colors"
                        >
                          Buscar
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">Logradouro</label>
                      <input
                        type="text"
                        name="logradouro"
                        value={cliente.logradouro}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Número</label>
                      <input
                        type="text"
                        name="numero"
                        value={cliente.numero}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Complemento</label>
                      <input
                        type="text"
                        name="complemento"
                        value={cliente.complemento}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Bairro</label>
                      <input
                        type="text"
                        name="bairro"
                        value={cliente.bairro}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Cidade</label>
                      <input
                        type="text"
                        name="cidade"
                        value={cliente.cidade}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Estado</label>
                      <select
                        name="estado"
                        value={cliente.estado}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      >
                        <option value="">Selecione</option>
                        {estados.map(uf => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">País</label>
                      <input
                        type="text"
                        name="pais"
                        value={cliente.pais}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                  </div>
                )}

                {/* Aba Contato */}
                {abaAtiva === "contato" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Telefone</label>
                      <input
                        type="text"
                        name="telefone"
                        value={formatarTelefone(cliente.telefone)}
                        onChange={(e) => handleChange({ target: { name: "telefone", value: e.target.value.replace(/\D/g, '') } })}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Celular</label>
                      <input
                        type="text"
                        name="celular"
                        value={formatarTelefone(cliente.celular)}
                        onChange={(e) => handleChange({ target: { name: "celular", value: e.target.value.replace(/\D/g, '') } })}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">E-mail</label>
                      <input
                        type="email"
                        name="email"
                        value={cliente.email}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                  </div>
                )}

                {/* Aba Comercial */}
                {abaAtiva === "comercial" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Limite de Crédito</label>
                      <div className="flex items-center border border-green-500 rounded">
                        <input
                          type="text"
                          name="limite_credito"
                          value={cliente.limite_credito}
                          onChange={(e) => handleChange({ target: { name: "limite_credito", value: e.target.value.replace(/\D/g, '') } })}
                          className="flex-1 p-2 rounded-l focus:ring-2 focus:ring-green-300"
                          placeholder="0,00"
                        />
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-r">R$</span>
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">Condição de Pagamento</label>
                      <input
                        type="text"
                        name="condicao_pagamento"
                        value={cliente.condicao_pagamento}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Ex: 30/60/90 dias"
                      />
                    </div>
                  </div>
                )}

                {/* Aba Outros */}
                {abaAtiva === "outros" && (
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                    <textarea
                      name="observacoes"
                      value={cliente.observacoes}
                      onChange={handleChange}
                      rows="5"
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Observações gerais sobre o cliente..."
                    />
                  </div>
                )}
              </div>

              {/* Botões do formulário */}
              <div className="flex gap-4 pt-6 mt-4 border-t">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                >
                  {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome, Documento ou E-mail</label>
                  <input
                    type="text"
                    placeholder="Digite o nome, CNPJ/CPF ou e-mail..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filtros.status}
                    onChange={(e) => setFiltros({...filtros, status: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todos</option>
                    {statusList.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Clientes */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <p className="text-gray-600">
                  {clientesFiltrados.length} cliente(s) encontrado(s)
                </p>
                <div className="text-sm text-gray-500">
                  Página {paginaAtual} de {totalPaginas || 1}
                </div>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome / Razão Social</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Documento</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Cidade/UF</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientesPaginados.map((clienteItem) => {
                          const statusInfo = getStatusInfo(clienteItem.status);
                          return (
                            <tr key={clienteItem.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                              <td className="p-3 text-sm font-mono">{clienteItem.id}</td>
                              <td className="p-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${clienteItem.tipo_pessoa === "J" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}`}>
                                  {clienteItem.tipo_pessoa === "J" ? "Jurídica" : "Física"}
                                </span>
                              </td>
                              <td className="p-3 text-sm font-medium">
                                <span className="flex items-center gap-1">
                                  {getNomeCliente(clienteItem)}
                                </span>
                              </td>
                              <td className="p-3 text-sm font-mono">{formatarDocumento(clienteItem)}</td>
                              <td className="p-3 text-sm">{clienteItem.cidade || "—"}{clienteItem.estado ? `/${clienteItem.estado}` : ""}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.cor}`}>
                                  {statusInfo.label}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditar(clienteItem)}
                                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                    title="Editar"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    onClick={() => handleExcluir(clienteItem.id)}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                    title="Excluir"
                                  >
                                    Excluir
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        
                        {clientesPaginados.length === 0 && (
                          <tr>
                            <td colSpan="7" className="p-8 text-center text-gray-500">
                              {filtros.busca || filtros.status
                                ? "Nenhum cliente encontrado com os filtros selecionados."
                                : "Nenhum cliente cadastrado."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {totalPaginas > 1 && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                      <button
                        onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                        disabled={paginaAtual === 1}
                        className={`px-4 py-2 rounded transition-colors ${
                          paginaAtual === 1
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      >
                        Anterior
                      </button>
                      <div className="flex gap-1">
                        {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(num => (
                          <button
                            key={num}
                            onClick={() => setPaginaAtual(num)}
                            className={`w-8 h-8 rounded transition-colors ${
                              paginaAtual === num
                                ? "bg-green-600 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                        disabled={paginaAtual === totalPaginas}
                        className={`px-4 py-2 rounded transition-colors ${
                          paginaAtual === totalPaginas
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}